import { Link } from "react-router-dom";
import { ArrowRight, Compass, Megaphone, MonitorSmartphone, Workflow, LineChart, Radar, LayoutTemplate, MessagesSquare, Settings2, Sparkles, Check, ShieldCheck } from "lucide-react";
import { IMAGES, CHALLENGES, CORE_SERVICES, PROCESS_STEPS, INDUSTRIES } from "../data/content";
import Reveal from "../components/Reveal";
import Seo, { LOCAL_BUSINESS_LD, ORGANIZATION_LD, SERVICE_CATALOG_LD } from "../components/Seo";

const ICON = { Compass, Megaphone, MonitorSmartphone, Workflow, LineChart, Radar, LayoutTemplate, MessagesSquare, Settings2, Sparkles };

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo
        title="Dallas Business Consulting, Marketing & Web Design"
        description="The Mogul Group Advisors provides business consulting, digital marketing, web design, branding, and operations support for startups and growing businesses throughout Dallas–Fort Worth."
        path="/"
        jsonLd={{ "@context": "https://schema.org", "@graph": [ORGANIZATION_LD, LOCAL_BUSINESS_LD, SERVICE_CATALOG_LD] }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="container-wide pt-16 md:pt-24 pb-16 md:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-6" data-testid="hero-overline">Business Growth Consulting · Dallas–Fort Worth Based · Serving Nationwide</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#0A0A0A]" data-testid="hero-headline">
              We Find What's <span className="italic text-gold">Slowing Your Business Down</span>—Then Help You Fix It.
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#4B5563] leading-relaxed max-w-2xl" data-testid="hero-supporting">
              Business strategy, brand growth, and operational systems for companies ready to move from activity to measurable growth.
            </p>
            <div className="mt-6 text-xs uppercase tracking-[0.2em] text-[#6B7280]">
              Business Growth &amp; Strategy · Brand &amp; Digital Growth · Operations &amp; Customer Experience
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/growth-assessment" data-testid="hero-cta-primary" className="btn btn-primary">
                Get My Mogul Score™ <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link to="/how-we-help" data-testid="hero-cta-secondary" className="btn btn-secondary">Explore How We Help</Link>
            </div>
            <p className="mt-8 text-sm text-[#6B7280]" data-testid="hero-credibility">
              We identify what's holding your business back and build the strategy to move it forward.
            </p>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative">
              <img src={IMAGES.hero} alt="Business advisor working with a local business owner in Dallas" className="w-full h-[520px] object-cover rounded-sm shadow-[0_12px_40px_rgba(0,0,0,0.12)]" />
              <div className="absolute -bottom-6 -left-6 hidden md:block bg-white p-5 border border-gray-100 shadow-[0_8px_30px_rgba(0,0,0,0.08)] max-w-[240px]">
                <div className="overline mb-2">Client Focus</div>
                <div className="font-serif text-lg leading-snug">Local businesses that keep DFW moving.</div>
              </div>
              <div className="absolute -top-6 -right-4 hidden md:block bg-[#0A0A0A] text-white p-5 max-w-[220px]">
                <div className="overline mb-2" style={{ color: "#C19B54" }}>Approach</div>
                <div className="text-sm leading-relaxed text-[#E5E7EB]">Hands-on strategy, brand, marketing, and operations under one roof.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHALLENGES / THE PROBLEM */}
      <section className="section bg-[#F7F7F8]">
        <div className="container-wide">
          <Reveal>
            <div className="max-w-3xl">
              <div className="overline mb-4">The Problem</div>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="challenges-headline">
                Working Harder Isn't Always the Answer.
              </h2>
              <p className="mt-6 text-[#4B5563] leading-relaxed">
                Your business may already have customers, revenue, a team, and a recognizable presence — but growth can still stall. Sometimes the problem isn't effort. It may be your positioning. Your marketing. Your customer acquisition. Your brand. Your operations. Or simply not knowing where the real bottleneck exists. <span className="text-[#0A0A0A] font-medium">That's where we come in.</span>
              </p>
            </div>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="challenges-grid">
            {CHALLENGES.map((c, i) => {
              const Icon = ICON[c.icon];
              return (
                <Reveal key={c.title} delay={i * 80}>
                  <div className="card-base h-full" data-testid={`challenge-card-${i}`}>
                    <Icon className="w-6 h-6 text-gold" strokeWidth={1.5} />
                    <h3 className="mt-6 font-serif text-xl leading-snug">{c.title}</h3>
                    <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">{c.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-12">
            <Link to="/growth-assessment" data-testid="challenges-cta" className="btn btn-secondary">
              Not Sure What's Holding Your Business Back? <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* CORE SERVICES */}
      <section className="section bg-white">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="overline mb-4">Three Practices</div>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="services-headline">
                Business Growth &amp; Strategy. Brand &amp; Digital Growth. Operations &amp; Customer Experience.
              </h2>
              <p className="mt-4 text-[#4B5563] leading-relaxed">Every engagement is built around one or more of these. Everything else supports them.</p>
            </div>
            <Link to="/services" data-testid="services-view-all" className="btn btn-ghost">View All Services <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6" data-testid="services-grid">
            {CORE_SERVICES.map((s, i) => {
              const Icon = ICON[s.icon];
              return (
                <Reveal key={s.slug} delay={i * 60}>
                  <div className="card-base h-full flex flex-col" data-testid={`service-card-${s.slug}`}>
                    <div className="w-11 h-11 rounded-sm bg-[#FDFBF7] flex items-center justify-center border border-[#EFE6D2]">
                      <Icon className="w-5 h-5 text-gold" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-serif text-xl leading-snug">{s.title}</h3>
                    <div className="mt-2 text-xs uppercase tracking-widest text-gold">{s.tagline}</div>
                    <p className="mt-4 text-sm text-[#4B5563] leading-relaxed flex-1">{s.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section bg-[#F7F7F8]">
        <div className="container-wide grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline mb-4">The Mogul Method™</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="process-headline">
              Assess → Strategize → Execute → Measure → Grow.
            </h2>
            <p className="mt-6 text-[#4B5563] leading-relaxed">
              One methodology. Every engagement runs through it. Strategy alone is a document — the Mogul Method™ makes strategy an operating rhythm that produces measurable change.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-6" data-testid="process-steps">
            {PROCESS_STEPS.map((p, i) => (
              <Reveal key={p.n} delay={i * 80}>
                <div className="bg-white border-l-2 border-gold p-8 flex gap-6" data-testid={`process-step-${i}`}>
                  <div className="font-serif text-4xl text-gold leading-none">{p.n}</div>
                  <div>
                    <h3 className="font-serif text-xl">{p.title}</h3>
                    <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES PREVIEW */}
      <section className="section bg-white">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="overline mb-4">Industries</div>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="industries-headline">
                Built for Local Businesses That Keep DFW Moving.
              </h2>
            </div>
            <Link to="/industries" data-testid="industries-view-all" className="btn btn-ghost">See Who We Serve <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {INDUSTRIES.slice(0, 8).map((ind, i) => (
              <Reveal key={ind.slug} delay={i * 50}>
                <div className="relative overflow-hidden group h-56" data-testid={`industry-tile-${ind.slug}`}>
                  <img src={ind.image} alt={ind.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-5">
                    <div className="text-white font-serif text-lg leading-tight">{ind.name}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WEBSITE / DIGITAL FEATURE */}
      <section className="section bg-[#F7F7F8]">
        <div className="container-wide grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <img src={IMAGES.laptop} alt="Website and mobile design mockup" className="w-full h-[460px] object-cover rounded-sm shadow-[0_12px_40px_rgba(0,0,0,0.10)]" />
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="overline mb-4">Digital Transformation</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="web-feature-headline">
              Your Online Presence Should Match the Quality of Your Business.
            </h2>
            <p className="mt-6 text-[#4B5563] leading-relaxed">
              We design professional websites and digital experiences that help customers understand your services, trust your company, and take the next step. Every website should be mobile-friendly, easy to navigate, and built around clear business goals.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {["Responsive website design","Service and product pages","Lead-generation forms","Contact and booking features","Search-engine foundations","Professional business email setup","Website updates and redesigns","Digital brand consistency"].map((b) => (
                <li key={b} className="flex gap-2 items-start"><Check className="w-4 h-4 mt-1 text-gold shrink-0" strokeWidth={2} />{b}</li>
              ))}
            </ul>
            <div className="mt-10">
              <Link to="/contact" data-testid="web-feature-cta" className="btn btn-primary">Discuss Your Website Project <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* GROWTH ASSESSMENT DARK CTA */}
      <section className="relative bg-[#0A0A0A] text-white grain">
        <div className="container-wide py-24 md:py-32 relative z-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-6" style={{ color: "#C19B54" }}>Free Business Growth Assessment</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]" data-testid="ga-cta-headline">
              Not Sure What's Holding Your Business Back?
            </h2>
            <p className="mt-6 text-[#D1D5DB] leading-relaxed max-w-2xl">
              Before we tell you what you need, we determine what's actually preventing your business from growing. The Mogul Growth Score™ evaluates strategy, marketing, brand, customer experience, operations, and revenue opportunities — then Alex personally reviews your results.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/growth-assessment" data-testid="ga-cta-primary" className="btn btn-primary">Get My Free Growth Assessment <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></Link>
              <Link to="/contact" data-testid="ga-cta-secondary" className="btn btn-dark">Let's Talk Growth</Link>
            </div>
            <p className="mt-8 text-xs text-[#A1A1AA] flex items-center gap-2"><ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> Your information will only be used to evaluate your business needs and respond to your request.</p>
          </div>
          <div className="lg:col-span-4">
            <div className="border border-white/10 p-8">
              <div className="overline" style={{ color: "#C19B54" }}>What we review</div>
              <ul className="mt-4 space-y-3 text-sm text-[#E5E7EB]">
                <li className="flex gap-2"><span className="text-gold">·</span> Current business goals</li>
                <li className="flex gap-2"><span className="text-gold">·</span> Marketing & online visibility</li>
                <li className="flex gap-2"><span className="text-gold">·</span> Website & brand experience</li>
                <li className="flex gap-2"><span className="text-gold">·</span> Operational bottlenecks</li>
                <li className="flex gap-2"><span className="text-gold">·</span> Practical next steps</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER PREVIEW */}
      <section className="section bg-white">
        <div className="container-wide grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img src={IMAGES.founder} alt="Shannen 'Alex' Alexander, Founder & CEO of The Mogul Group Advisors" className="w-full h-[540px] object-cover object-top rounded-sm" />
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-4">Meet the Founder</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="founder-headline">
              From Operational Chaos to Structured Growth.
            </h2>
            <p className="mt-6 text-[#4B5563] leading-relaxed">
              Shannen "Alex" Alexander founded The Mogul Group after 15+ years leading high-volume hospitality operations and enterprise sales at national organizations like T-Mobile and Uber Technologies. The firm was built to give business owners the practical, hands-on support they need to strengthen strategy, operations, and leadership.
            </p>
            <blockquote className="mt-6 border-l-2 border-gold pl-5 italic text-[#0A0A0A] text-lg font-serif">
              "Help owners build companies that run with clarity, strong leadership, and sustainable growth."
              <span className="block mt-2 not-italic text-xs uppercase tracking-widest text-[#6B7280]">— Alex, Founder &amp; CEO</span>
            </blockquote>
            <div className="mt-10">
              <Link to="/founder" data-testid="founder-cta" className="btn btn-secondary">Meet the Founder <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#F7F7F8]">
        <div className="container-wide py-24 text-center max-w-3xl mx-auto">
          <div className="overline mb-4">Let's Talk</div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="final-cta-headline">
            Let's Build the Next Stage of Your Business.
          </h2>
          <p className="mt-6 text-[#4B5563] leading-relaxed">
            Whether you need a stronger website, consistent marketing, clearer operations, or a complete business strategy, The Mogul Group Advisors can help you determine the right next move.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/growth-assessment" data-testid="final-cta-primary" className="btn btn-primary">Get My Free Growth Assessment</Link>
            <Link to="/contact" data-testid="final-cta-secondary" className="btn btn-secondary">Let's Talk Growth</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
